import { FC } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar, VariantType, closeSnackbar } from "notistack";
import { X } from "lucide-react";

import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";
import { StorageAPI } from "../storage.api";
import { StorageMutationResponse } from "../storage.api.types";

import { DefaultStorageForm } from "./DefaultStorageForm";
import { SignatureFieldRef } from "@/Features/Signatures";
import {
  StorageCaseInfoInput,
  StorageFormData,
  EditStorageFormData,
  StorageDefaultInfo,
} from "../storage.types";
import { processCustomAttributesForAPI } from "../storage.utils";

export type CreateStorageResult = StorageMutationResponse & {
  showView?: boolean;
};

interface CreateStorageFormProps {
  width?: number | undefined;
  onSubmit: (result?: CreateStorageResult) => void;
  onCancel: () => void;
  copyFromData?: StorageFormData;
  caseInfo?: StorageCaseInfoInput;
  defaultInfo?: StorageDefaultInfo;
}

export const CreateStorageForm: FC<CreateStorageFormProps> = ({
  width,
  onSubmit,
  onCancel,
  copyFromData,
  caseInfo,
  defaultInfo,
}) => {
  const navigate = useNavigate();
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  // Whitelist the copyable fields — copy sources are often full table rows,
  // and ids, CoC/assign fields (location_id, timestamp) and computed columns
  // must not seed a new item.
  const getCopySourceData = (): StorageFormData | undefined => {
    if (!copyFromData) return undefined;
    return {
      storage_number: "",
      type: copyFromData.type,
      // serial_number is intentionally not copied — it's a per-device
      // identifier; carrying it over risks two rows sharing one serial.
      capacity: copyFromData.capacity,
      capacity_unit: copyFromData.capacity_unit,
      model_name: copyFromData.model_name,
      model_number: copyFromData.model_number,
      make: copyFromData.make,
      encryption_key: copyFromData.encryption_key,
      is_general_storage: copyFromData.is_general_storage,
      notes: copyFromData.notes,
      custom_attributes: copyFromData.custom_attributes,
    };
  };

  const handleSubmit = async (
    data: StorageFormData,
    _diffData?: EditStorageFormData,
    signatures?: { assignedBySig: SignatureFieldRef | undefined }
  ) => {
    try {
      const source: Record<string, unknown> = {
        ...data,
        case_id: caseInfo?.case_id,
      };

      // Extract custom attributes. Unlike PATCH — where an empty value is a
      // "clear this field" signal — the create endpoint inserts every posted
      // row verbatim, so drop empty values to avoid junk attribute rows.
      const { cleaned: formatted, customAttributes } =
        processCustomAttributesForAPI(source);
      const populatedAttributes = customAttributes.filter(
        ({ value }) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
      );
      // Form state also carries the copy source's raw custom_attributes
      // array; the flattened keys are authoritative, so never post it.
      delete formatted.custom_attributes;
      if (populatedAttributes.length > 0) {
        formatted.custom_attributes = populatedAttributes;
      }

      // Assign/Intake details at creation
      const ipAddress = await getIpAddress();
      const assignedBySig = signatures?.assignedBySig;
      const signature: Record<string, unknown> = {};

      signature.custody_from_sig =
        assignedBySig && !assignedBySig.isEmpty?.()
          ? {
              signature: assignedBySig.getSignature?.(),
              signer: currentUser.full_name,
              timestamp: moment().toISOString(),
              userAgent: window.navigator.userAgent,
              ip_address: ipAddress,
              monolith_user: {
                first_name: currentUser.first_name,
                last_name: currentUser.last_name,
                full_name: currentUser.full_name,
                email: currentUser.email,
                title: currentUser.title,
                user_id: currentUser.user_id,
              },
              esig: assignedBySig.isESignature,
            }
          : null;

      // Populate fields similar to legacy modal
      formatted.signature = JSON.stringify(signature);
      formatted.date_added = db_timestamp();
      formatted.entered_by = currentUser.full_name;
      formatted.entered_by_id = currentUser.user_id;
      formatted.custody_from = "Storage Cache";
      formatted.reason = formatted.reason || "Storage item assigned to case.";
      // The API inserts this value raw into MySQL, so it must be
      // "YYYY-MM-DD HH:mm:ss" (UTC) — never ISO-with-Z.
      formatted.timestamp = formatted.timestamp
        ? db_timestamp(formatted.timestamp as string)
        : db_timestamp();

      const result: CreateStorageResult = await StorageAPI.create(formatted);
      // The API layer resolves (never rejects) on HTTP errors, and the server
      // can also return 200 with { success: false } — treat both as failure.
      if (!result || result.success === false) {
        throw new Error("Failed to create storage item");
      }

      // Signal to parent to show the view modal
      result.showView = true;
      result.storage_id = result.storage_id || result?.data?.storage_id;

      const storageId = result.storage_id;
      // Items created from the storage section linger longer so the VIEW ITEM
      // action stays reachable; within a case the default creation toast is kept.
      const isCaseContext = !!caseInfo?.case_id;
      enqueueSnackbar(`Storage Item Created.`, {
        variant: "success" as VariantType,
        autoHideDuration: isCaseContext ? 5000 : 10000,
        action: (key) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Deliberately not an mfui Button: notistack's portal mounts
                above MonolithUIProvider (SnackbarProvider is in index.tsx),
                so themed components crash here with an undefined theme. */}
            <span
              onClick={() => {
                navigate(`/storage/${storageId}`);
                closeSnackbar(key);
              }}
              style={{
                color: "#ffffff",
                fontWeight: "bold",
                cursor: "pointer",
                padding: "4px 8px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                borderRadius: "4px",
                border: "1px solid #ffffff",
                fontSize: "0.55rem",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor =
                  "rgba(255, 255, 255, 0.3)";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor =
                  "rgba(255, 255, 255, 0.2)";
              }}
            >
              VIEW ITEM
            </span>
            <span
              onClick={() => closeSnackbar(key)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                cursor: "pointer",
                color: "#ffffff",
                transition: "background-color 0.2s",
              }}
            >
              <X size={16} />
            </span>
          </div>
        ),
      });

      if (caseInfo?.case_id) {
        enqueueSnackbar(`Storage Item Assigned to Case.`, {
          variant: "success" as VariantType,
        });
      }

      onSubmit?.(result);
      return result;
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this storage item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>
        {copyFromData ? "Copy Storage Item" : "Create Storage"}
      </FormTitle>
      <DefaultStorageForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        DefaultFormData={getCopySourceData()}
        buttonProps={{
          submitText: copyFromData ? "Copy Storage" : "Create Storage",
        }}
        caseId={caseInfo?.case_id || undefined}
        includeCustomFields={true}
        defaultInfo={defaultInfo}
      />
    </FormWrapper>
  );
};
